import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8">
        <div className="flex items-center gap-2">
          <ShieldCheck size={24} className="text-slate-900" />
          <span className="font-['Inter'] text-xl font-bold tracking-tight">TAGVerify</span>
        </div>
        <h1 className="mt-6 flex items-center gap-2 font-['Inter'] text-2xl font-bold tracking-tight text-slate-900">
          <KeyRound size={20} /> Set a new password
        </h1>
        {!token ? (
          <p className="mt-4 text-sm text-slate-500">
            This link is missing its reset token. Request a new link from the{" "}
            <button onClick={() => navigate("/login")} className="font-medium text-blue-600 underline">sign-in page</button>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input id="new-password" type="password" required minLength={8} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                data-testid="reset-password-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input id="confirm-password" type="password" required value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password"
                data-testid="reset-confirm-input" className="h-11" />
            </div>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{error}</div>
            )}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button"
              className="h-11 w-full rounded-full bg-primary text-base font-semibold hover:bg-primary/90">
              {loading ? "Updating..." : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
